// What every reader on the page shares: one request per question, and the
// last answer to it.
//
// WHY IT EXISTS. `useRead` held its answer per hook, so every component that
// asked the same question asked the server separately. Opening My Team asked
// `GET /home` six times at once and `GET /schedule` three, and asked them all
// again a second later when the lineup arrived and the screen's body mounted
// (measured 2026-10-07, against the dev database). `/home` is about a hundred
// statements, so the page cost six times what it should and waited behind its
// own duplicates. The screens were written believing a second ask was free —
// LineupScreen's comment says the home view "already asked for, so neither is
// a second request" — and this is what makes that true.
//
// THE RULES, each a guard against showing something false:
//
//   ONE REQUEST PER QUESTION AT A TIME. A reader that asks while the same
//   question is in flight waits for that answer instead of sending its own.
//
//   AN ANSWER IS SHARED. Whoever asked, every reader of that question hears
//   it, so a reload on one panel updates the same read on every other.
//
//   A RECENT ANSWER IS NOT ASKED AGAIN (B-263). For `FRESH_MS` after it
//   arrived, a reader that mounts takes the held answer and asks nothing. Past
//   that it shows the held answer at once, marked stale, and asks again. The
//   window is a timer, and a timer may not count a device asleep, so a page
//   that is hidden ends every window (`expire`): nothing comes back from a
//   closed lid looking fresh.
//
//   A WRITE ENDS IT ALL. When any write this tab makes has answered, nothing
//   held is drawn for a reader that mounts after it and nothing in flight is
//   joined, because either may have been read before the write landed: a
//   player just dropped must not stand on the next screen's roster, looking
//   current, until the new answer comes. That reader loads as it always did.
//   (The first cut drew the old answer as stale; the review of 2026-10-07
//   found that stale is silent while a refresh is in flight.)
//
//   A REFUSAL IS AN ANSWER. A good answer is replaced by the next one, and a
//   refusal drops it: a league you were just taken out of is not drawn from
//   what was held. No answer at all (unreachable) keeps it, out of its window.
//
//   A RELOAD ALWAYS ASKS, but joins a request that started after the last
//   write: two panels reloading for the same move send one request.
//
//   ANOTHER ACCOUNT STARTS EMPTY. `clear` drops everything when the person
//   signed in changes, so nobody's league is drawn, even for a moment, under
//   somebody else's name.
//
// No React, no network and no clock: `read.ts` hands in the fetch and a
// timer, and `readStore.test.ts` drives the rules as calls. The window is a
// timer rather than two readings of the time, because this layer reads the
// machine clock for nothing but an answer's arrival (ICD-U01 §7 invariant 5):
// "fresh" is a duration, and a duration needs no time of day.

import type { Reply } from './api';

/** How long an answer is used without asking again (B-263). */
export const FRESH_MS = 30_000;

/** How many questions' answers are kept. The oldest unwatched go first. */
export const CAPACITY = 64;

type Listener = (reply: Reply<unknown>) => void;

interface HeldAnswer {
  reply: Reply<unknown> & { status: 'ok' };
  /** The write epoch it was asked in. */
  epoch: number;
  /** Still inside its window; the timer clears it. */
  inWindow: boolean;
  /** Stops that timer, when a newer answer replaces this one. */
  cancel: () => void;
}

interface Entry {
  /** The newest good answer. */
  held: HeldAnswer | null;
  /** The request in flight, the epoch it was sent in, and what it asked. */
  flight: { epoch: number; seq: number; path: string } | null;
  /** The newest request sent; an older one answering later is not the answer. */
  seq: number;
  listeners: Set<Listener>;
  /** Which ask touched it last, for eviction: a count, not a time. */
  touched: number;
  /** What it was last asked as, so it can be asked again (`refreshWatched`). */
  path: string | null;
  /** Settles when the request in flight has answered, whatever it said. */
  landing: Promise<void> | null;
}

export interface ReadStoreOptions {
  fetch: (path: string) => Promise<Reply<unknown>>;
  /** Run `fn` once `ms` have passed; returns the way to cancel it. */
  after: (ms: number, fn: () => void) => () => void;
  freshMs?: number;
  capacity?: number;
}

export interface Held {
  reply: Reply<unknown> & { status: 'ok' };
  /** Asked in this epoch and within the window: use it and ask nothing. */
  fresh: boolean;
}

export interface ReadStore {
  /** The newest good answer to this question, if there is one. */
  peek(identity: string): Held | null;
  /**
   * Make sure an answer is coming. Joins one in flight unless it was sent
   * before the last write; `force` asks even when the held answer is fresh.
   */
  ask(identity: string, path: string, options?: { force?: boolean }): void;
  /** Hear every answer to this question from now on. */
  subscribe(identity: string, listener: Listener): () => void;
  /** A write has answered: nothing held or in flight counts as current. */
  wrote(): void;
  /** The page was hidden: nothing held is fresh, though all of it may be drawn. */
  expire(): void;
  /** The account changed: forget everything. */
  clear(): void;
  /**
   * Ask again every question somebody is watching now — a phone's pull to
   * refresh (B-312) — keeping what is drawn until each answers. Settles once
   * every one of them has.
   */
  refreshWatched(): Promise<void>;
}

export function createReadStore(options: ReadStoreOptions): ReadStore {
  const freshMs = options.freshMs ?? FRESH_MS;
  const capacity = options.capacity ?? CAPACITY;
  const entries = new Map<string, Entry>();
  // Bumped by every write and every account change. An answer asked in an
  // older epoch may predate the write, so it is drawn but not trusted.
  let epoch = 0;
  let seq = 0;
  let touches = 0;
  const isFresh = (held: HeldAnswer) => held.inWindow && held.epoch === epoch;

  const entryOf = (identity: string): Entry => {
    let entry = entries.get(identity);
    if (entry === undefined) {
      entry = {
        held: null,
        flight: null,
        seq: 0,
        listeners: new Set(),
        touched: ++touches,
        path: null,
        landing: null,
      };
      entries.set(identity, entry);
      evict(identity);
    }
    return entry;
  };

  // The oldest first, and never one somebody is watching or waiting on, nor
  // the one being asked about this moment.
  const evict = (keep?: string) => {
    if (entries.size <= capacity) return;
    const idle = [...entries.entries()]
      .filter(([id, e]) => id !== keep && e.listeners.size === 0 && e.flight === null)
      .sort((a, b) => a[1].touched - b[1].touched);
    for (const [identity, e] of idle.slice(0, entries.size - capacity)) {
      e.held?.cancel();
      entries.delete(identity);
    }
  };

  const send = (identity: string, entry: Entry, path: string) => {
    seq += 1;
    const mine = seq;
    const sentIn = epoch;
    entry.seq = mine;
    entry.flight = { epoch: sentIn, seq: mine, path };
    entry.landing = options.fetch(path).then((reply) => {
      // Overtaken by a newer ask, which answers everybody waiting on this one,
      // or forgotten while it was out: not the answer to tell anyone.
      if (entries.get(identity) !== entry || entry.seq !== mine) return;
      entry.flight = null;
      if (reply.status === 'ok') {
        entry.held?.cancel();
        const held: HeldAnswer = { reply, epoch: sentIn, inWindow: true, cancel: () => undefined };
        held.cancel = options.after(freshMs, () => {
          held.inWindow = false;
        });
        entry.held = held;
      } else if (reply.status === 'refused') {
        entry.held?.cancel();
        entry.held = null;
      } else if (entry.held !== null) {
        entry.held.inWindow = false;
      }
      for (const listener of [...entry.listeners]) listener(reply);
    });
  };

  return {
    peek(identity) {
      const entry = entries.get(identity);
      // Asked before the last write: not drawn at all (see the rules above).
      if (entry === undefined || entry.held === null || entry.held.epoch !== epoch) return null;
      return { reply: entry.held.reply, fresh: isFresh(entry.held) };
    },

    ask(identity, path, { force = false } = {}) {
      const entry = entryOf(identity);
      entry.touched = ++touches;
      entry.path = path;
      if (entry.flight !== null && entry.flight.epoch === epoch) return;
      if (!force && entry.held !== null && isFresh(entry.held)) return;
      send(identity, entry, path);
    },

    subscribe(identity, listener) {
      const entry = entryOf(identity);
      entry.listeners.add(listener);
      return () => {
        entry.listeners.delete(listener);
        evict();
      };
    },

    wrote() {
      epoch += 1;
    },

    expire() {
      for (const entry of entries.values()) if (entry.held !== null) entry.held.inWindow = false;
    },

    refreshWatched() {
      const landings: Promise<void>[] = [];
      for (const [identity, entry] of entries) {
        if (entry.listeners.size === 0 || entry.path === null) continue;
        // One already on its way since the last write is the fresh answer.
        if (entry.flight === null || entry.flight.epoch !== epoch)
          send(identity, entry, entry.path);
        if (entry.landing !== null) landings.push(entry.landing);
      }
      return Promise.all(landings).then(() => undefined);
    },

    // A reader still mounted keeps its place, and one waiting on an answer
    // asked as the previous account is asked again as this one, rather than
    // left waiting for an answer that will now never be told to it.
    clear() {
      epoch += 1;
      for (const [identity, entry] of [...entries.entries()]) {
        entry.held?.cancel();
        entry.held = null;
        if (entry.listeners.size === 0) entries.delete(identity);
        else if (entry.flight !== null) send(identity, entry, entry.flight.path);
      }
    },
  };
}
